import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { getCookieDomain } from '../lib/util/cookieDomain';
import { DEV_MODE } from '../lib/util/devMode';
import { getFiGrowthBook } from '../lib/growthbook';
import { typedWindow } from '../lib/Window';
import { configActions } from '../reducers/config';
import { storeShopActions } from '../reducers/storeShop';
import { series3UpgradeShopActions } from '../reducers/series3UpgradeShop';
import { subscriptionShopActions } from '../reducers/subscriptionShop';
import { verizonShopActions } from '../reducers/verizonShop';
import { CollarProductId, miniCollarId, series3PlusCollarId, ultraCollarId } from '../types';
import * as types from '../types';
import i18n from '../../i18n';
import { logInternalError } from '../lib/errors';

/** Countries by their ISO-2 code
 * Please ensure that the string value of the enum matches the ISO-2 code
 * @see supportedCountries for the countries that are actively supported */
export enum FiCountry {
  US = 'US',
  CA = 'CA',
  GB = 'GB',
  FR = 'FR',
  DE = 'DE',
  ES = 'ES',
  FI = 'FI',
  MT = 'MT',
  RO = 'RO',
  AT = 'AT',
  SI = 'SI',
  LV = 'LV',
  SK = 'SK',
  BE = 'BE',
  LU = 'LU',
  LI = 'LI',
  NL = 'NL',
  SE = 'SE',
  DK = 'DK',
  NO = 'NO',
  CH = 'CH',
  IS = 'IS',
  TR = 'TR',
}

export enum FiRegion {
  NA = 'NA',
  EU = 'EU',
}

export enum FiLanguage {
  EN_US = 'en-US',
  EN_CA = 'en-CA',
  FR_CA = 'fr-CA',
  EN_GB = 'en-GB',
  DE_DE = 'de-DE',
  ES_ES = 'es-ES',
  FR_FR = 'fr-FR',
  EN_FI = 'en-FI',
  EN_MT = 'en-MT',
  RO_RO = 'ro-RO',
  DE_AT = 'de-AT',
  EN_SI = 'en-SI',
  EN_LV = 'en-LV',
  EN_SK = 'en-SK',
  NL_BE = 'nl-BE',
  FR_LU = 'fr-LU',
  DE_LU = 'de-LU',
  DE_LI = 'de-LI',
  NL_NL = 'nl-NL',
  EN_SE = 'en-SE',
  EN_DK = 'en-DK',
  EN_NO = 'en-NO',
  DE_CH = 'de-CH',
  EN_IS = 'en-IS',
  EN_TR = 'en-TR',
  FR_BE = 'fr-BE',
  SK_SK = 'sk-SK',
  LV_LV = 'lv-LV',
  TR_TR = 'tr-TR',
  EN_CH = 'en-CH',
}

/**
 * Map FiLanguage to the i18next language code used for translation bundles.
 * English-only locales (en-US, en-FI, en-MT, en-SI, en-LV, en-SK) map to the base 'en' bundle.
 */
export function fiLanguageToI18nCode(language: FiLanguage): string {
  switch (language) {
    case FiLanguage.EN_US:
    case FiLanguage.EN_FI:
    case FiLanguage.EN_MT:
    case FiLanguage.EN_SI:
    case FiLanguage.EN_LV:
    case FiLanguage.EN_SK:
    case FiLanguage.EN_SE:
    case FiLanguage.EN_DK:
    case FiLanguage.EN_NO:
    case FiLanguage.EN_IS:
    case FiLanguage.EN_TR:
    case FiLanguage.EN_CH:
      return 'en';
    case FiLanguage.NL_BE:
      return 'nl-be';
    case FiLanguage.DE_CH:
      return 'de-ch';
    case FiLanguage.FR_LU:
      return 'fr-lu';
    case FiLanguage.DE_LU:
      return 'de-lu';
    case FiLanguage.DE_LI:
      return 'de-li';
    case FiLanguage.NL_NL:
      return 'nl-nl';
    case FiLanguage.DE_AT:
      return 'de-de';
    case FiLanguage.FR_BE:
      return 'fr-be';
    case FiLanguage.SK_SK:
      return 'sk-sk';
    case FiLanguage.LV_LV:
      return 'lv-lv';
    case FiLanguage.TR_TR:
      return 'tr-tr';
    default:
      return language.toLowerCase();
  }
}

interface LocaleContextValue {
  country: FiCountry;
  setCountry: (country: FiCountry) => void;
  language: FiLanguage;
  setLanguage: (language: FiLanguage) => void;
  region: FiRegion;
  currency: types.FiCurrency;
  /** Used for money amounts where we want the numbers after the decimal point, e.g. $99.00 */
  currencyFormatter: Intl.NumberFormat;
  /** Used for money amounts where we only want whole numbers, e.g. $99 */
  wholeNumberCurrencyFormatter: Intl.NumberFormat;
  /** List of countries supported by Fi */
  supportedCountries: FiCountry[];
  /** Flag emoji for the current country */
  flagEmoji: string;
  /** Used to specify a currency while keeping the formatting of the current locale */
  getFormatterForCurrency: (currency: types.FiCurrency) => Intl.NumberFormat;
}

export const collarProductIsSupportedInCountry = (
  productId: CollarProductId,
  country: FiCountry,
  internalTester?: boolean,
) => {
  if (productId === series3PlusCollarId) return true;
  if (productId === miniCollarId) {
    return getFiGrowthBook().getFeatureValue<FiCountry[]>('mini-supported-countries', [FiCountry.US]).includes(country);
  }
  if (productId === ultraCollarId) {
    const attrs = internalTester === undefined ? undefined : { internalTester };
    const isUltraExperienceEnabled = getFiGrowthBook()
      ? getFiGrowthBook().getFeatureValue<boolean>('ultra-experience', false, attrs)
      : false;

    return (
      isUltraExperienceEnabled &&
      getFiGrowthBook()
        .getFeatureValue<FiCountry[]>('ultra-supported-countries', [FiCountry.US], attrs)
        .includes(country)
    );
  }
  return country === FiCountry.US;
};

export const supplementsProductIsSupportedInCountry = (country: FiCountry) => {
  return getFiGrowthBook()
    .getFeatureValue<FiCountry[]>('supplements-supported-countries', [FiCountry.US])
    .includes(country);
};

export const nanoProductIsSupportedInCountry = (country: FiCountry) => {
  return getFiGrowthBook().getFeatureValue<FiCountry[]>('nano-supported-countries', [FiCountry.US]).includes(country);
};

export const makersAccessoriesProductIsSupportedInCountry = (country: FiCountry) => {
  return country === FiCountry.US || country === FiCountry.CA;
};

const MINI_SKU = 'M1-MUA';

const LocaleContext = React.createContext<LocaleContextValue>({
  country: FiCountry.US,
  setCountry: () => {},
  language: FiLanguage.EN_US,
  setLanguage: () => {},
  region: FiRegion.NA,
  currency: types.FiCurrency.USD,
  currencyFormatter: new Intl.NumberFormat(FiLanguage.EN_US, {
    style: 'currency',
    currency: types.FiCurrency.USD,
  }),
  wholeNumberCurrencyFormatter: new Intl.NumberFormat(FiLanguage.EN_US, {
    style: 'currency',
    currency: types.FiCurrency.USD,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  supportedCountries: [FiCountry.US, FiCountry.CA],
  flagEmoji: '🇺🇸',
  getFormatterForCurrency: (currency) => {
    return new Intl.NumberFormat(getCurrencyFormatterLocale(FiLanguage.EN_US, FiCountry.US), {
      style: 'currency',
      currency: currency,
    });
  },
});

const FI_COUNTRY_COOKIE = 'fi_country';
const VIEWER_COUNTRY_COOKIE = 'viewer_country';
const FI_LANGUAGE_COOKIE = 'fi_language';

/**
 * Determines the initial country from cookies. The fi_country cookie (explicit
 * user selection via the country selector) is validated against the FiCountry
 * enum rather than GrowthBook targeting. This avoids a race condition where
 * countries gated behind async targeting attributes (e.g. internalTester) get
 * rejected before siteConfig loads, destroying the user's saved preference.
 *
 * The viewer_country cookie (CloudFront GeoIP) is validated against the
 * GrowthBook supported list since it's auto-detected, not a user choice.
 */
function getInitialCountry(): FiCountry {
  try {
    const explicit = Cookies.get(FI_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
    if (explicit && validCountries.includes(explicit)) return explicit;

    const supportedCountries = getFiGrowthBook().getFeatureValue<FiCountry[]>('ecom-supported-countries', [
      FiCountry.US,
    ]);
    const viewer = Cookies.get(VIEWER_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
    if (viewer && supportedCountries.includes(viewer)) return viewer;
    return FiCountry.US;
  } catch {
    return FiCountry.US;
  }
}

const validCountries = Object.values(FiCountry);

/**
 * Gets the country code from cookies, checking fi_country (explicit user choice)
 * first, then falling back to viewer_country (CloudFront GeoIP) for users who
 * never explicitly selected a country (e.g., Instagram in-app browser traffic).
 */
export function getCountryCodeFromCookie(): FiCountry {
  const explicit = Cookies.get(FI_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
  if (explicit && validCountries.includes(explicit)) {
    return explicit;
  }
  const viewer = Cookies.get(VIEWER_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
  if (viewer && validCountries.includes(viewer)) {
    return viewer;
  }
  return FiCountry.US;
}

const DEFAULT_SUPPORTED_LANGUAGES: FiLanguage[] = [
  FiLanguage.EN_US,
  FiLanguage.EN_CA,
  FiLanguage.FR_CA,
  FiLanguage.EN_GB,
  FiLanguage.FR_FR,
  FiLanguage.DE_DE,
  FiLanguage.ES_ES,
  FiLanguage.EN_FI,
  FiLanguage.EN_MT,
  FiLanguage.RO_RO,
  FiLanguage.DE_AT,
  FiLanguage.EN_SI,
  FiLanguage.EN_LV,
  FiLanguage.EN_SK,
  FiLanguage.NL_BE,
  FiLanguage.FR_LU,
  FiLanguage.DE_LU,
  FiLanguage.DE_LI,
  FiLanguage.NL_NL,
  FiLanguage.EN_SE,
  FiLanguage.EN_DK,
  FiLanguage.EN_NO,
  FiLanguage.DE_CH,
  FiLanguage.EN_IS,
  FiLanguage.EN_TR,
  FiLanguage.EN_CH,
  FiLanguage.FR_BE,
  FiLanguage.SK_SK,
  FiLanguage.LV_LV,
  FiLanguage.TR_TR,
];

function getGrowthBookSupportedLanguages(): FiLanguage[] {
  const raw = getFiGrowthBook().getFeatureValue<string[]>('ecom-supported-languages', DEFAULT_SUPPORTED_LANGUAGES);
  return raw.map(apiLanguageToFiLanguage).filter((l): l is FiLanguage => l != null);
}

function detectBrowserLanguage(supported: FiLanguage[]): FiLanguage | undefined {
  const browserLang = navigator.language ?? navigator.languages?.[0];
  if (!browserLang) return undefined;

  const lang = browserLang.toLowerCase();

  if (lang.startsWith('fr-ca') && supported.includes(FiLanguage.FR_CA)) {
    return FiLanguage.FR_CA;
  }

  if (lang.startsWith('fr-lu') && supported.includes(FiLanguage.FR_LU)) {
    return FiLanguage.FR_LU;
  }

  if (lang.startsWith('fr') && supported.includes(FiLanguage.FR_FR)) {
    return FiLanguage.FR_FR;
  }

  if (lang.startsWith('fr') && supported.includes(FiLanguage.FR_CA)) {
    return FiLanguage.FR_CA;
  }

  if (lang.startsWith('en-ca') && supported.includes(FiLanguage.EN_CA)) {
    return FiLanguage.EN_CA;
  }

  if (lang.startsWith('en-gb') && supported.includes(FiLanguage.EN_GB)) {
    return FiLanguage.EN_GB;
  }

  if (lang.startsWith('de-li') && supported.includes(FiLanguage.DE_LI)) {
    return FiLanguage.DE_LI;
  }

  if (lang.startsWith('de-ch') && supported.includes(FiLanguage.DE_CH)) {
    return FiLanguage.DE_CH;
  }

  if (lang.startsWith('de-lu') && supported.includes(FiLanguage.DE_LU)) {
    return FiLanguage.DE_LU;
  }

  if (lang.startsWith('de-at') && supported.includes(FiLanguage.DE_AT)) {
    return FiLanguage.DE_AT;
  }

  if (lang.startsWith('de') && supported.includes(FiLanguage.DE_DE)) {
    return FiLanguage.DE_DE;
  }

  if (lang.startsWith('es') && supported.includes(FiLanguage.ES_ES)) {
    return FiLanguage.ES_ES;
  }

  if (lang.startsWith('en-fi') && supported.includes(FiLanguage.EN_FI)) {
    return FiLanguage.EN_FI;
  }

  if (lang.startsWith('en-mt') && supported.includes(FiLanguage.EN_MT)) {
    return FiLanguage.EN_MT;
  }

  if (lang.startsWith('en-si') && supported.includes(FiLanguage.EN_SI)) {
    return FiLanguage.EN_SI;
  }

  if (lang.startsWith('nl-nl') && supported.includes(FiLanguage.NL_NL)) {
    return FiLanguage.NL_NL;
  }

  if (lang.startsWith('nl') && supported.includes(FiLanguage.NL_BE)) {
    return FiLanguage.NL_BE;
  }

  if (lang.startsWith('ro') && supported.includes(FiLanguage.RO_RO)) {
    return FiLanguage.RO_RO;
  }

  if (lang.startsWith('sk') && supported.includes(FiLanguage.SK_SK)) {
    return FiLanguage.SK_SK;
  }

  if (lang.startsWith('lv') && supported.includes(FiLanguage.LV_LV)) {
    return FiLanguage.LV_LV;
  }

  if (lang.startsWith('tr') && supported.includes(FiLanguage.TR_TR)) {
    return FiLanguage.TR_TR;
  }

  if (lang.startsWith('en') && supported.includes(FiLanguage.EN_US)) {
    return FiLanguage.EN_US;
  }

  return undefined;
}

function getInitialLanguage(): FiLanguage {
  // Check explicit cookie preference — accept any valid FiLanguage regardless of country
  const cookieValue = Cookies.get(FI_LANGUAGE_COOKIE) as FiLanguage | undefined;
  const allLanguages: string[] = Object.values(FiLanguage);
  if (cookieValue && allLanguages.includes(cookieValue)) {
    return cookieValue;
  }

  // Browser detection — respect browser language regardless of detected country
  const allSupported = getGrowthBookSupportedLanguages();
  const browserLanguage = detectBrowserLanguage(allSupported);
  if (browserLanguage) {
    return browserLanguage;
  }

  return FiLanguage.EN_US;
}

// Hook to remove mini collar from cart if country is not US
// Both marketingsite and ecommerce use the same cookie, and we don't
// have a way to compare previous vs current country without extra state.
// Which is why we can't clear the entire cart on country change from tryfi.
// So we just run this effect once on mount to clean up unsupported items.
function useRemoveUnsupportedItemsFromCart(country: FiCountry) {
  const [onMount, setOnMount] = useState(true);
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!onMount) {
      return;
    }

    if (country === FiCountry.US) {
      return;
    }

    Object.entries(cart.cartItems).forEach(([key, item]) => {
      if (item.lineItem.sku !== MINI_SKU) {
        return;
      }

      dispatch(storeShopActions.removeCartItem({ id: key }));
    });

    setOnMount(false);
  }, [onMount, country, cart, dispatch]);
}

/**
 * when en-CA users view CAD prices, use en-US locale to produce "CA$" disambiguation
 * instead of bare "$" (which en-CA locale produces since CAD is the local currency).
 * all other language/currency combos use the user's actual language locale.
 */
function getCurrencyFormatterLocale(language: FiLanguage, country: FiCountry): string {
  if (language === FiLanguage.EN_CA && getCurrencyForCountry(country) === types.FiCurrency.CAD) {
    return FiLanguage.EN_US;
  }
  return language;
}

export function getCurrencyForCountry(country: FiCountry): types.FiCurrency {
  const cadEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-use-cad', false);
  const gbpEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-use-gbp', false);

  if (cadEnabled && country === FiCountry.CA) {
    return types.FiCurrency.CAD;
  }
  if (gbpEnabled && country === FiCountry.GB) {
    return types.FiCurrency.GBP;
  }
  if (
    country === FiCountry.FR ||
    country === FiCountry.DE ||
    country === FiCountry.ES ||
    country === FiCountry.FI ||
    country === FiCountry.MT ||
    country === FiCountry.AT ||
    country === FiCountry.SI ||
    country === FiCountry.LV ||
    country === FiCountry.SK ||
    country === FiCountry.BE ||
    country === FiCountry.LU ||
    country === FiCountry.NL
  ) {
    return types.FiCurrency.EUR;
  }
  if (country === FiCountry.LI || country === FiCountry.CH) {
    return types.FiCurrency.CHF;
  }
  if (country === FiCountry.SE) {
    return types.FiCurrency.SEK;
  }
  if (country === FiCountry.DK) {
    return types.FiCurrency.DKK;
  }
  if (country === FiCountry.NO) {
    return types.FiCurrency.NOK;
  }
  if (country === FiCountry.IS) {
    return types.FiCurrency.ISK;
  }
  if (country === FiCountry.TR) {
    return types.FiCurrency.TRY;
  }
  if (country === FiCountry.RO) {
    return types.FiCurrency.RON;
  }
  return types.FiCurrency.USD;
}

/**
 * Converts a country string (e.g. from billing address) to a FiCountry enum.
 * Returns `null` for any unsupported or unrecognized country codes.
 */
export function getFiCountryFromString(country: string) {
  const match = validCountries.find((c) => c.toString() === country.toUpperCase());
  if (match) {
    return match;
  }
  return null;
}

/**
 * Converts `FiCountry` to Product type `ProductRegion`
 */
export function getProductRegionFromFiCountry(country: FiCountry) {
  switch (country) {
    case FiCountry.US:
      return types.ProductRegion.US;
    case FiCountry.CA:
      return types.ProductRegion.CA;
    case FiCountry.GB:
      return types.ProductRegion.GB;
    case FiCountry.FR:
      return types.ProductRegion.FR;
    case FiCountry.DE:
      return types.ProductRegion.DE;
    case FiCountry.ES:
      return types.ProductRegion.ES;
    case FiCountry.FI:
      return types.ProductRegion.FI;
    case FiCountry.MT:
      return types.ProductRegion.MT;
    case FiCountry.RO:
      return types.ProductRegion.RO;
    case FiCountry.AT:
      return types.ProductRegion.AT;
    case FiCountry.SI:
      return types.ProductRegion.SI;
    case FiCountry.LV:
      return types.ProductRegion.LV;
    case FiCountry.SK:
      return types.ProductRegion.SK;
    case FiCountry.BE:
      return types.ProductRegion.BE;
    case FiCountry.LU:
      return types.ProductRegion.LU;
    case FiCountry.LI:
      return types.ProductRegion.LI;
    case FiCountry.NL:
      return types.ProductRegion.NL;
    case FiCountry.SE:
      return types.ProductRegion.SE;
    case FiCountry.DK:
      return types.ProductRegion.DK;
    case FiCountry.NO:
      return types.ProductRegion.NO;
    case FiCountry.CH:
      return types.ProductRegion.CH;
    case FiCountry.IS:
      return types.ProductRegion.IS;
    case FiCountry.TR:
      return types.ProductRegion.TR;
  }
}

export function LocaleContextProvider({ children }: { children: React.ReactNode }) {
  const [country, setCountryState] = useState<FiCountry>(getInitialCountry());
  const [language, setLanguageState] = useState<FiLanguage>(getInitialLanguage());
  const dispatch = useDispatch();
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);

  useRemoveUnsupportedItemsFromCart(country);

  const setFiCountryCookie = useCallback((value: FiCountry) => {
    Cookies.remove(FI_COUNTRY_COOKIE, { path: '/' }); // clear hostname-scoped shadow
    Cookies.set(FI_COUNTRY_COOKIE, value, {
      expires: 30, // 30 day expiration
      domain: getCookieDomain(),
      secure: !DEV_MODE,
      sameSite: 'lax',
      path: '/',
    });
  }, []);

  const setFiLanguageCookie = useCallback((value: FiLanguage) => {
    Cookies.set(FI_LANGUAGE_COOKIE, value, {
      expires: 30, // 30 day expiration
      domain: getCookieDomain(),
      secure: !DEV_MODE,
      sameSite: 'lax',
      path: '/',
    });
  }, []);

  const setCountry = useCallback(
    (newCountry: FiCountry) => {
      if (country === newCountry) return;

      setCountryState(newCountry);
      setFiCountryCookie(newCountry);

      // Update Decagon chat metadata when country changes
      if (typedWindow.duet) {
        typedWindow.duet.setMetadata({
          userRegion: newCountry,
          locale: language,
        });
      }

      // Clear country-specific promotions when country changes. Cart items
      // are validated server-side via `validateCart` in `CountrySelector`,
      // which selectively removes any items that are not available in the
      // newly selected country.
      for (const shop of [storeShopActions, series3UpgradeShopActions, subscriptionShopActions, verizonShopActions]) {
        dispatch(shop.removeCoupon());
        if ('removeReferral' in shop) {
          dispatch(shop.removeReferral());
        }
        if ('removeGiftCard' in shop) {
          dispatch(shop.removeGiftCard());
        }
      }

      // Redo app config initialization since products may be affected by the country change
      dispatch(configActions.clearConfig());
    },
    [country, language, dispatch, setFiCountryCookie],
  );

  const setLanguage = useCallback(
    (newLanguage: FiLanguage) => {
      if (language === newLanguage) return;

      setLanguageState(newLanguage);
      setFiLanguageCookie(newLanguage);

      // Update Decagon chat metadata when language changes
      if (typedWindow.duet) {
        typedWindow.duet.setMetadata({
          locale: newLanguage,
        });
      }
    },
    [language, setFiLanguageCookie],
  );

  useEffect(() => {
    const existing = Cookies.get(FI_COUNTRY_COOKIE)?.toUpperCase();
    if (existing !== country) {
      setFiCountryCookie(country);
    }
  }, [country, setFiCountryCookie]);

  // Sync GrowthBook attributes so targeting-dependent flags re-evaluate when
  // the user switches country or when internalTester resolves from siteConfig.
  useEffect(() => {
    void getFiGrowthBook().setAttributes({ countryCode: country, internalTester });
  }, [country, internalTester]);

  useEffect(() => {
    const existing = Cookies.get(FI_LANGUAGE_COOKIE)?.toLowerCase();
    if (existing !== language) {
      setFiLanguageCookie(language);
    }
  }, [language, setFiLanguageCookie]);

  useEffect(() => {
    const target = fiLanguageToI18nCode(language);
    if (i18n.language !== target) {
      i18n.changeLanguage(target);
    }
  }, [language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const otLang = language.slice(0, 2).toLowerCase();
    try {
      window.OneTrust?.changeLanguage?.(otLang);
    } catch (err) {
      logInternalError(err);
    }
  }, [language]);

  const currencyFormatter = useMemo<Intl.NumberFormat>(
    () =>
      new Intl.NumberFormat(getCurrencyFormatterLocale(language, country), {
        style: 'currency',
        currency: getCurrencyForCountry(country),
      }),
    [language, country],
  );

  const wholeNumberCurrencyFormatter = useMemo<Intl.NumberFormat>(
    () =>
      new Intl.NumberFormat(getCurrencyFormatterLocale(language, country), {
        style: 'currency',
        currency: getCurrencyForCountry(country),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [language, country],
  );

  const getFormatterForCurrency = useCallback(
    (currency: types.FiCurrency) => {
      return new Intl.NumberFormat(getCurrencyFormatterLocale(language, country), {
        style: 'currency',
        currency: currency,
      });
    },
    [language, country],
  );

  const supportedCountries = useMemo(
    () =>
      getFiGrowthBook().getFeatureValue<FiCountry[]>('ecom-supported-countries', [FiCountry.US], { internalTester }),
    [internalTester],
  );

  const flagEmoji = useMemo(() => {
    switch (country) {
      case FiCountry.CA:
        return '🇨🇦';
      case FiCountry.GB:
        return '🇬🇧';
      case FiCountry.FR:
        return '🇫🇷';
      case FiCountry.DE:
        return '🇩🇪';
      case FiCountry.ES:
        return '🇪🇸';
      case FiCountry.FI:
        return '🇫🇮';
      case FiCountry.MT:
        return '🇲🇹';
      case FiCountry.RO:
        return '🇷🇴';
      case FiCountry.AT:
        return '🇦🇹';
      case FiCountry.SI:
        return '🇸🇮';
      case FiCountry.LV:
        return '🇱🇻';
      case FiCountry.SK:
        return '🇸🇰';
      case FiCountry.BE:
        return '🇧🇪';
      case FiCountry.LU:
        return '🇱🇺';
      case FiCountry.LI:
        return '🇱🇮';
      case FiCountry.NL:
        return '🇳🇱';
      case FiCountry.SE:
        return '🇸🇪';
      case FiCountry.DK:
        return '🇩🇰';
      case FiCountry.NO:
        return '🇳🇴';
      case FiCountry.CH:
        return '🇨🇭';
      case FiCountry.IS:
        return '🇮🇸';
      case FiCountry.TR:
        return '🇹🇷';
      case FiCountry.US:
      default:
        return '🇺🇸';
    }
  }, [country]);

  const region = useMemo(() => {
    switch (country) {
      case FiCountry.CA:
      case FiCountry.US:
        return FiRegion.NA;
      case FiCountry.GB:
      case FiCountry.FR:
      case FiCountry.DE:
      case FiCountry.ES:
      case FiCountry.FI:
      case FiCountry.MT:
      case FiCountry.RO:
      case FiCountry.AT:
      case FiCountry.SI:
      case FiCountry.LV:
      case FiCountry.SK:
      case FiCountry.BE:
      case FiCountry.LU:
      case FiCountry.LI:
      case FiCountry.NL:
      case FiCountry.SE:
      case FiCountry.DK:
      case FiCountry.NO:
      case FiCountry.CH:
      case FiCountry.IS:
      case FiCountry.TR:
        return FiRegion.EU;
      default:
        return FiRegion.NA;
    }
  }, [country]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      country,
      setCountry,
      language,
      setLanguage,
      region,
      currency: getCurrencyForCountry(country),
      currencyFormatter,
      wholeNumberCurrencyFormatter,
      supportedCountries,
      flagEmoji,
      getFormatterForCurrency,
    }),
    [
      country,
      setCountry,
      language,
      setLanguage,
      region,
      currencyFormatter,
      wholeNumberCurrencyFormatter,
      supportedCountries,
      flagEmoji,
      getFormatterForCurrency,
    ],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}

const SESSION_LOCALE_KEY_PREFIX = 'hasAppliedSessionLocale_';

export function clearSessionLocaleFlag() {
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(SESSION_LOCALE_KEY_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  }
}

/**
 * Marks the session locale as already applied for a given user so that
 * useApplySessionLocale will skip overriding the current locale. Used
 * during login to preserve the locale the user was browsing with (e.g.
 * de-DE) instead of letting the backend's stored language replace it.
 */
export function markSessionLocaleApplied(userId: string) {
  sessionStorage.setItem(`${SESSION_LOCALE_KEY_PREFIX}${userId}`, 'true');
}

/**
 * Returns a callback that applies a backend session's language/region to the
 * locale context exactly once per browser session per user. Subsequent calls
 * (e.g. page refreshes) are no-ops so the user's local country-selector
 * preference is preserved.
 */
export function useApplySessionLocale() {
  const { setLanguage, setCountry } = useLocale();

  return useCallback(
    (userId: string, sessionLanguage: string | null | undefined, sessionRegion: string | null | undefined) => {
      const key = `${SESSION_LOCALE_KEY_PREFIX}${userId}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, 'true');

      const language = apiLanguageToFiLanguage(sessionLanguage);
      if (language) setLanguage(language);

      const region = apiRegionToFiCountry(sessionRegion);
      if (region) setCountry(region);
    },
    [setLanguage, setCountry],
  );
}

/**
 * Maps a backend language enum name (e.g. "EN_US", "FR_CA") to the frontend
 * FiLanguage value (e.g. "en-US", "fr-CA"). Returns undefined for null/invalid input.
 */
export function apiLanguageToFiLanguage(apiLang: string | null | undefined): FiLanguage | undefined {
  if (!apiLang) return undefined;
  const fiValues = Object.values(FiLanguage) as string[];
  // Already a valid FiLanguage value (BCP-47 format like "en-US")
  if (fiValues.includes(apiLang)) return apiLang as FiLanguage;
  // Backend uses "EN_US" format; frontend uses "en-US" (BCP-47)
  const parts = apiLang.split('_');
  if (parts.length !== 2) return undefined;
  const mapped = `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
  return fiValues.includes(mapped) ? (mapped as FiLanguage) : undefined;
}

/**
 * Maps a backend region enum name (e.g. "US", "CA") to the frontend FiCountry value.
 * Returns undefined for null/invalid input.
 */
export function apiRegionToFiCountry(apiRegion: string | null | undefined): FiCountry | undefined {
  if (!apiRegion) return undefined;
  const upper = apiRegion.toUpperCase();
  return Object.values(FiCountry).find((c) => c === upper);
}

export function fiCurrencyToGQL(currency: types.FiCurrency) {
  switch (currency) {
    case types.FiCurrency.USD:
      return types.gqlTypes.Currency.USD;
    case types.FiCurrency.CAD:
      return types.gqlTypes.Currency.CAD;
    case types.FiCurrency.GBP:
      return types.gqlTypes.Currency.GBP;
    case types.FiCurrency.EUR:
      return types.gqlTypes.Currency.EUR;
    case types.FiCurrency.RON:
      return types.gqlTypes.Currency.RON;
    case types.FiCurrency.SEK:
      return types.gqlTypes.Currency.SEK;
    case types.FiCurrency.DKK:
      return types.gqlTypes.Currency.DKK;
    case types.FiCurrency.NOK:
      return types.gqlTypes.Currency.NOK;
    case types.FiCurrency.CHF:
      return types.gqlTypes.Currency.CHF;
    case types.FiCurrency.ISK:
      return types.gqlTypes.Currency.ISK;
    case types.FiCurrency.TRY:
      return types.gqlTypes.Currency.TRY;
  }
}

export default LocaleContext;
